<template>
  <!-- FIRST in the DOM on purpose, before the page: iOS reads the text just
       BEFORE a text box to decide what it is for, and page text (or what
       Matt types, which may well say "pan") must not be there. So the box is
       first in the panel's DOM and the page comes after the whole panel.
       On SCREEN the box sits at the bottom (CSS `order`), under the
       conversation running oldest to newest, like any messaging app
       (Matt, 2026-10-12). See CLAUDE.md. -->
  <aside class="teacher" :class="{ 'teacher--open': open, 'teacher--talking': talking, 'teacher--left': side === 'left' }">
    <button type="button" class="teacher__tab" @click="open = !open">
      <span class="teacher__tab-dot" aria-hidden="true"></span>
      {{ open ? 'Hide' : 'Teacher' }}<span v-if="!open && unread" class="teacher__badge">{{ unread }}</span>
    </button>
    <div class="teacher__panel">
      <div class="teacher__ask">
        <label for="teacher-input" class="teacher__label">Ask your teacher</label>
        <textarea id="teacher-input" v-model="draft" class="talk teacher__input" name="message" autocomplete="off" autocorrect="on" autocapitalize="sentences" spellcheck="true" rows="2" :placeholder="placeholder" :disabled="busy" @keydown.enter.exact.prevent="send"></textarea>
        <div class="row">
          <button type="button" class="btn btn-primary" :disabled="busy || talking || !draft.trim()" @click="send">{{ busy && !talking ? 'Thinking…' : 'Send' }}</button>
          <button v-if="talking" type="button" class="btn btn-primary" @click="endTalk">Stop talking</button>
          <button v-else-if="canTalk" type="button" class="btn" :disabled="busy" @click="startTalk">Talk out loud</button>
        </div>
        <div v-if="talking" class="teacher__talk" aria-live="polite">
          <p class="teacher__talk-state">{{ talkLabel }}</p>
          <p v-if="heard && talkState === 'listening'" class="teacher__heard">{{ heard }}</p>
        </div>
        <p v-if="error" class="teacher__error">{{ error }}</p>
      </div>
      <div ref="log" class="teacher__log">
        <div v-for="turn in history" :key="turn.at + turn.role" class="teacher__turn" :class="`teacher__turn--${turn.role}`">
          <p class="teacher__text">{{ turn.text }}</p>
          <p v-if="turn.where?.label" class="teacher__where">{{ turn.where.label }}</p>
        </div>
        <p v-if="!history.length" class="muted teacher__empty">This is your teacher — one person, across the whole course. Ask anything, any time: before, during, after. They remember.</p>
      </div>
    </div>
  </aside>
</template>

<script>
// The teacher, present on every screen.
//
// Matt, 2026-10-02: "a single page app that maintains an open chat with
// Claude ... It should feel like a single person. A single teacher who
// remembers what we've done and can continuously tailor my experience."
// One conversation for the household (<hat>/cooking/chat), followed live on
// every device; with every message the teacher gets where you are, the
// household's copy of the recipe, their own notes, the debriefs, tonight's
// pace and the preferences — and whatever they decide to do comes back as
// actions, applied here right away (utils/teacher.js).
//
// Wide screens: a column beside the page. Phones: a drawer from the bottom.
// Talk out loud is the voice loop from the old AskBox (utils/voice.js).
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { trackEvent } from '../utils/usage.js';
import { useRoute } from 'vue-router';
import { store, sendCommand } from '../state.js';
import { askTeacher } from '../utils/api.js';
import { subscribe } from '../utils/live.js';
import { dbPut } from '../store/db.js';
import { chatPath, cleanTurns, gatherContext, pace, recipeForTeacher, applyActions, MAX_CHAT_TURNS } from '../utils/teacher.js';
import { ensureRecipe } from '../utils/recipes.js';
import { logTrace } from '../utils/trace.js';
import { submitBugReport } from '../utils/bugReports.js';
import { isStopPhrase, listenOnce, primeSpeech, speak, stopSpeaking, voiceSupported } from '../utils/voice.js';

const TALK_QUIET_LIMIT_MS = 5 * 60 * 1000;
const FAST_EMPTY_MS = 600;
const FAST_EMPTY_LIMIT = 4;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const PLACEHOLDERS = {
  cook: '“Is that enough colour?”',
  lesson: '“Can I make any of this ahead?”',
  week: '“Which night should the roast go on?”',
  debrief: '“Was the sauce supposed to be that thin?”',
  home: '“What should I learn next?”'
};

export default {
  name: 'TeacherChat',
  props: { hat: { type: String, default: null } },
  setup (props) {
    const route = useRoute();
    const where = computed(() => store.state.where);
    const side = computed(() => store.state.preferences?.chatSide || 'right');
    const open = ref(false);
    const draft = ref('');
    const busy = ref(false);
    const error = ref(null);
    const history = ref([]);
    const seen = ref(0);
    const unread = computed(() => Math.max(0, history.value.filter((t) => t.role !== 'you').length - seen.value));
    const placeholder = computed(() => PLACEHOLDERS[where.value.screen] || PLACEHOLDERS.home);
    watch(open, (isOpen) => { if (isOpen) seen.value = history.value.filter((t) => t.role !== 'you').length; });

    // --- the shared conversation ----------------------------------------------
    let unsubscribe = null;
    let writing = false;
    function follow () {
      unsubscribe?.();
      unsubscribe = null;
      if (!props.hat) return;
      unsubscribe = subscribe(chatPath(props.hat), (turns) => {
        if (writing) return;
        const shared = cleanTurns(turns);
        if (JSON.stringify(shared) !== JSON.stringify(history.value)) history.value = shared;
        if (open.value) seen.value = history.value.filter((t) => t.role !== 'you').length;
      });
    }
    watch(() => props.hat, follow, { immediate: true });
    onBeforeUnmount(() => unsubscribe?.());
    async function save () {
      if (!props.hat) return;
      writing = true;
      try { await dbPut(chatPath(props.hat), history.value.slice(-MAX_CHAT_TURNS)); } catch { /* the next turn tries again */ } finally { writing = false; }
    }
    const whereLabel = () => {
      const w = where.value;
      if (w.screen === 'cook') return w.phase === 'prep' ? `prep ${(w.stepIndex || 0) + 1}` : w.phase === 'steps' ? `step ${(w.stepIndex || 0) + 1}` : 'after cooking';
      return { lesson: 'lesson page', week: 'the week', debrief: 'debrief', home: 'home' }[w.screen] || w.screen;
    };
    const push = (role, text) => { history.value = [...history.value, { role, text, at: Date.now(), where: { screen: where.value.screen, lessonId: where.value.lessonId || null, label: whereLabel() } }]; };

    // --- one message ------------------------------------------------------------
    async function ask (text, { voice = false } = {}) {
      busy.value = true;
      error.value = null;
      const w = { ...where.value };
      const priorTurns = history.value.filter((t) => t.role !== 'did').slice(-14);
      push('you', text);
      await save();
      try {
        const [recipe, context] = await Promise.all([
          w.lessonId ? ensureRecipe(props.hat, w.lessonId) : Promise.resolve(null),
          gatherContext(props.hat, w, { preferences: store.state.preferences })
        ]);
        if (w.screen === 'cook' && w.lessonId) logTrace(props.hat, w.lessonId, { type: 'asked', phase: w.phase, stepIndex: w.stepIndex, text: text.slice(0, 200) });
        const { answer, actions } = await askTeacher({
          question: text, where: w, recipe: recipeForTeacher(recipe), history: priorTurns, voice,
          notes: context.notes, debriefs: context.debriefs, plan: context.plan, pace: pace(context.trace, recipe), preferences: context.preferences,
          probe: store.state.probe
        });
        push('teacher', answer);
        const did = await applyActions(props.hat, actions, { lessonId: w.lessonId, sendCommand, fileReport: (text) => submitBugReport(store, text, route) });
        for (const line of did) push('did', line);
        await save();
        return answer;
      } catch (err) {
        error.value = err.message;
        draft.value = text;
        history.value = history.value.slice(0, -1);
        await save();
        return null;
      } finally {
        busy.value = false;
      }
    }
    async function send () {
      trackEvent('teacher-asked');
      const text = draft.value.trim();
      if (!text || busy.value) return;
      draft.value = '';
      open.value = true;
      await ask(text);
    }

    // --- talking out loud ---------------------------------------------------------
    const canTalk = ref(false);
    onMounted(() => { canTalk.value = voiceSupported(); });
    const talking = ref(false);
    const talkState = ref('');
    const heard = ref('');
    let listening = null;
    const talkLabel = computed(() => ({
      listening: 'Listening… say “thanks” when you’re done.',
      thinking: 'Thinking…',
      speaking: 'Talking… (Stop talking cuts it off)'
    })[talkState.value] || '');
    function endTalk () {
      talking.value = false;
      talkState.value = '';
      heard.value = '';
      listening?.stop();
      listening = null;
      stopSpeaking();
    }
    async function startTalk () {
      trackEvent('teacher-talked');
      if (talking.value || busy.value) return;
      primeSpeech();
      open.value = true;
      talking.value = true;
      error.value = null;
      let quietSince = Date.now();
      let fastEmpties = 0;
      // How the last answer's end was noticed, kept until the mic reopens so
      // the trace can say how long the gap was (bug report, 2026-10-27).
      let spoken = null;
      while (talking.value) {
        talkState.value = 'listening';
        heard.value = '';
        const startedAt = Date.now();
        let text = '';
        const after = spoken;
        spoken = null;
        const onListening = after && (() => {
          const w = where.value;
          if (w.screen === 'cook' && w.lessonId) logTrace(props.hat, w.lessonId, { type: 'voice', ended: after.how, spokeMs: after.ms, micMs: Date.now() - after.at });
        });
        try {
          listening = listenOnce({ onWords: (words) => { heard.value = words; }, onListening });
          text = await listening.promise;
        } catch (err) {
          if (talking.value) error.value = err.message;
          break;
        } finally {
          listening = null;
        }
        if (!talking.value) break;
        if (!text) {
          fastEmpties = Date.now() - startedAt < FAST_EMPTY_MS ? fastEmpties + 1 : 0;
          if (fastEmpties >= FAST_EMPTY_LIMIT) { error.value = 'The microphone keeps stopping. Tap Talk out loud to try again, or type.'; break; }
          if (Date.now() - quietSince > TALK_QUIET_LIMIT_MS) { error.value = 'Stopped listening after a long quiet. Tap Talk out loud to pick it up again.'; break; }
          await wait(250);
          continue;
        }
        fastEmpties = 0;
        quietSince = Date.now();
        if (isStopPhrase(text)) break;
        talkState.value = 'thinking';
        const answer = await ask(text, { voice: true });
        if (!talking.value) break;
        talkState.value = 'speaking';
        spoken = { ...(await speak(answer == null ? 'Sorry, I could not reach your teacher. Try again in a moment.' : answer)), at: Date.now() };
        if (!talking.value) break;
      }
      endTalk();
    }
    const onHidden = () => { if (document.visibilityState === 'hidden' && talking.value) endTalk(); };
    onMounted(() => document.addEventListener('visibilitychange', onHidden));
    onBeforeUnmount(() => { document.removeEventListener('visibilitychange', onHidden); endTalk(); });

    // The drawer closes itself on a phone when the page changes underneath it.
    watch(() => route.path, () => { if (window.innerWidth < 900) open.value = false; });

    // Newest message is at the bottom, beside the box: keep it in view when
    // the panel opens and whenever a turn lands (ours, theirs, or another
    // device's).
    const log = ref(null);
    const toNewest = async () => { await nextTick(); if (log.value) log.value.scrollTop = log.value.scrollHeight; };
    watch(() => history.value.length, toNewest);
    watch(open, (isOpen) => { if (isOpen) toNewest(); });
    onMounted(toNewest);
    return { open, draft, busy, error, history, unread, placeholder, side, send, canTalk, talking, talkState, talkLabel, heard, startTalk, endTalk, log };
  }
};
</script>

<style scoped>
.teacher { --teacher-width: min(38vw, 30rem); }
.teacher__tab { display: none; }
.teacher__panel { display: flex; flex-direction: column; gap: 0.75rem; }
.teacher__ask { display: flex; flex-direction: column; gap: 0.5rem; order: 2; }
.teacher__label { font-size: 0.8rem; color: var(--muted); }
.teacher__input { min-height: 4.2rem; }
.teacher__log { display: flex; flex-direction: column; gap: 0.6rem; overflow-y: auto; }
.teacher__turn { padding: 0.6rem 0.8rem; border-radius: var(--radius); background: var(--surface); border: 1px solid var(--line); }
.teacher__turn--you { background: var(--accent-soft); border-color: transparent; }
.teacher__turn--did { background: transparent; border-style: dashed; font-size: 0.85rem; color: var(--muted); }
.teacher__text { margin: 0; white-space: pre-wrap; line-height: 1.4; }
.teacher__where { margin: 0.25rem 0 0; font-size: 0.72rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; }
.teacher__empty { font-size: 0.9rem; }
.teacher__talk { font-size: 0.9rem; color: var(--muted); }
.teacher__talk-state, .teacher__heard { margin: 0; }
.teacher__heard { font-style: italic; }
.teacher__error { color: var(--accent); margin: 0; font-size: 0.9rem; }

/* Wide: a column beside the page, the whole height under the header, its
   log scrolling. The row it sits in is exactly what the header leaves of the
   screen (styles.css), so the column simply fills it: no sticky, no sum of
   the header's height to keep in step. */
@media (min-width: 900px) {
  .teacher { order: 2; width: var(--teacher-width); flex: 0 0 var(--teacher-width); min-height: 0; padding: 1rem 1rem calc(var(--safe-bottom, 0px) + 1rem); box-sizing: border-box; border-left: 1px solid var(--line); background: var(--bg); }
  .teacher--left { order: 0; border-left: 0; border-right: 1px solid var(--line); }
  .teacher__panel { height: 100%; }
  .teacher__log { flex: 1; min-height: 0; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
}

/* Narrow: a drawer from the bottom with a tab that is always there. */
@media (max-width: 899px) {
  .teacher { position: fixed; left: 0; right: 0; bottom: 0; z-index: 60; transform: translateY(calc(100% - 3.1rem)); transition: transform 0.25s ease; background: var(--bg); border-top: 1px solid var(--line); border-radius: 18px 18px 0 0; box-shadow: 0 -8px 30px rgba(0,0,0,0.12); max-height: 85vh; display: flex; flex-direction: column; }
  .teacher--open { transform: translateY(0); }
  .teacher__tab { display: flex; align-items: center; justify-content: center; gap: 0.5rem; width: 100%; min-height: 3.1rem; background: none; border: 0; font: inherit; font-weight: 700; color: var(--text); }
  .teacher__tab-dot { width: 0.7rem; height: 0.7rem; border-radius: 50%; background: var(--accent); }
  .teacher__badge { background: var(--accent); color: #fff; border-radius: 999px; font-size: 0.75rem; padding: 0.05rem 0.5rem; margin-left: 0.3rem; }
  .teacher__panel { padding: 0 1rem calc(var(--safe-bottom, 0px) + 1rem); overflow: hidden; min-height: 0; }
  .teacher__log { flex: 1; min-height: 0; max-height: 45vh; }
  /* The house bug button floats bottom-left, where Send would be. */
  .teacher__ask .row { justify-content: flex-end; }
}
</style>
